import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddIcon, ImportIcon } from "../../assets/composer-editor-icons";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

/**
 * Inline + / import menus for a unit tree row (add: lesson, role play, info page, and a unit inside
 * this one; import: three file flows via sidebar hook).
 *
 * `canHoldUnits` is false at the deepest level units may nest to, where **Unit** is left out rather
 * than offered and refused.
 */
export default function UnitTreeRowTrailingActions({
  unitId,
  courseId,
  canHoldUnits = false,
  unitImportHandlers,
}) {
  const navigate = useNavigate();
  const [addAnchor, setAddAnchor] = useState(null);
  const [importAnchor, setImportAnchor] = useState(null);

  return (
    <>
      <Tooltip title="Import content" placement="top" arrow enterDelay={400} disableInteractive>
        <button
          type="button"
          className="pd-tree-node-action pd-tree-node-action--import"
          aria-label="Import content"
          aria-haspopup="true"
          aria-expanded={Boolean(importAnchor)}
          onClick={(e) => {
            e.stopPropagation();
            setImportAnchor(e.currentTarget);
          }}
        >
          <ImportIcon />
        </button>
      </Tooltip>
      <Menu
        anchorEl={importAnchor}
        open={Boolean(importAnchor)}
        onClose={() => setImportAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true }}
      >
        <MenuItem
          onClick={() => {
            setImportAnchor(null);
            unitImportHandlers.onImportLesson?.(unitId);
          }}
        >
          Import Lesson
        </MenuItem>
        <MenuItem
          onClick={() => {
            setImportAnchor(null);
            unitImportHandlers.onImportLessonFolder?.(unitId);
          }}
        >
          Import Lesson from Folder
        </MenuItem>
        <MenuItem
          onClick={() => {
            setImportAnchor(null);
            unitImportHandlers.onImportScenario?.(unitId);
          }}
        >
          Import Roleplay
        </MenuItem>
        <MenuItem
          onClick={() => {
            setImportAnchor(null);
            unitImportHandlers.onImportScenarioFolder?.(unitId);
          }}
        >
          Import Roleplay from Folder
        </MenuItem>
        <MenuItem
          onClick={() => {
            setImportAnchor(null);
            unitImportHandlers.onImportInfoPage?.(unitId);
          }}
        >
          Import Info Page
        </MenuItem>
        <MenuItem
          onClick={() => {
            setImportAnchor(null);
            unitImportHandlers.onImportInfoPageFolder?.(unitId);
          }}
        >
          Import Info Page from Folder
        </MenuItem>
      </Menu>
      <Tooltip title="Add content" placement="top" arrow enterDelay={400} disableInteractive>
        <button
          type="button"
          className="pd-tree-node-action"
          aria-label="Add content"
          aria-haspopup="true"
          aria-expanded={Boolean(addAnchor)}
          onClick={(e) => {
            e.stopPropagation();
            setAddAnchor(e.currentTarget);
          }}
        >
          <AddIcon />
        </button>
      </Tooltip>
      <Menu
        anchorEl={addAnchor}
        open={Boolean(addAnchor)}
        onClose={() => setAddAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true }}
      >
        <MenuItem
          onClick={() => {
            setAddAnchor(null);
            navigate(`/composer/courses/edit-lesson/new?unitId=${unitId}`);
          }}
        >
          Lesson
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAddAnchor(null);
            navigate(`/composer/courses/edit-scenario/new?unitId=${unitId}`);
          }}
        >
          Roleplay
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAddAnchor(null);
            navigate(`/composer/courses/edit-info-page/new?unitId=${unitId}`);
          }}
        >
          Info page
        </MenuItem>
        {canHoldUnits && courseId != null ? (
          <MenuItem
            onClick={() => {
              setAddAnchor(null);
              navigate(
                `/composer/courses/edit-unit/new?courseId=${courseId}&parentUnitId=${unitId}`
              );
            }}
          >
            Unit
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}
